import { ShieldCheck, HeartHandshake, Target, Scale } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { useSiteContent } from "../hooks/useSiteContent";

const values = [
  { icon: Target, title: "Precision", desc: "Methodical sweeps, documented findings, zero guesswork." },
  { icon: HeartHandshake, title: "Integrity", desc: "Honest advice about the level of cover you actually need." },
  { icon: ShieldCheck, title: "Non-Intrusive Safety", desc: "Protection that doesn't disrupt the people it protects." },
  { icon: Scale, title: "Public Trust", desc: "Accredited teams working to British Standards, every deployment." },
];

const accreditations = ["NASDU", "BS 8517-1", "BS 8517-2", "SIA Licensed", "NCTAS-P"];

export default function About() {
  const content = useSiteContent();
  const about = content?.about || {};
  const pg = content?.pages?.about_page || {};
  const story = Array.isArray(about.story) ? about.story : [];

  return (
    <main className="bg-brand-obsidian pt-28 sm:pt-32" data-testid="about-page">
      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "About Us"}</Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {about.title || "Excellence in Canine Security"}
            </h1>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <div className="space-y-5" data-testid="about-story">
                {story.length > 0 ? (
                  story.map((p, i) => (
                    <p key={i} className="text-slate-300 leading-relaxed text-base sm:text-lg">{p}</p>
                  ))
                ) : (
                  <p className="text-slate-400">Our story is being written - come back soon.</p>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative group" data-testid="about-image-frame">
                <div className="absolute -inset-2 bg-gradient-to-br from-brand-crimson/40 to-transparent rounded-lg blur-lg opacity-60 group-hover:opacity-100 transition-opacity" />
                <img
                  src={about.image}
                  alt="Quest K9's handler on deployment"
                  className="relative rounded-lg border border-brand-line w-full h-[380px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 bg-brand-surface/40 border-y border-brand-line" data-testid="values-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.values_eyebrow || "What We Stand For"}</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">{pg.values_title || "Values that walk the walk"}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-lg border border-brand-line bg-brand-obsidian p-7 hover:border-brand-crimson/60 transition-colors duration-300" data-testid={`value-card-${i}`}>
                  <v.icon className="h-7 w-7 text-brand-crimson" />
                  <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6" data-testid="accreditations-section">
        <div className="mx-auto max-w-7xl text-center">
          <Reveal>
            <Eyebrow>{pg.acc_eyebrow || "Accreditation & Compliance"}</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{pg.acc_title || "Fully credentialed. Fully insured."}</h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {accreditations.map((a, i) => (
              <Reveal key={a} delay={i * 0.06}>
                <div
                  data-testid={`accreditation-badge-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="px-8 py-5 rounded-lg border border-brand-line bg-brand-surface font-eyebrow uppercase tracking-[0.2em] font-bold text-white/80 hover:text-white hover:border-brand-crimson/70 transition-all duration-300"
                >
                  {a}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
